import React, { useEffect, useState } from "react";
import { Box, Card, Typography, Checkbox, FormControlLabel, Button, IconButton } from "@mui/material";
import { Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";
import DynamicInput from "./dynamic-input";
import {
  GROUP_TYPE,
  TRIP_TYPE,
  TRIPAGEOPTION,
  TRIPDAYSOPTION,
  TRIP_SCOPE,
  COUNTRY_OPTIONS,
  DOMESTIC,
  INTERNATIONAL,
  DEFAULT_DOMESTIC_COUNTRY,
  AIRPORT_COUNTRY_OPTIONS,
  getStatesForCountry,
  getPrimaryAirport,
} from "../utils/utils";
import { TripFormValues } from "../types/tripTypes";
import { FormikProps } from "formik";
import CountryStateDistrict from "utils/Data";

interface LocationOption {
  states: any[];
  districts: any[];
}

interface LocationOptionsState {
  startLoc: LocationOption[];
  endLoc: LocationOption[];
  destination: LocationOption[];
}

interface TripInfoFormProps {
  formik: FormikProps<TripFormValues>;
}

const TripInfoForm: React.FC<TripInfoFormProps> = ({ formik }) => {
  const [locationOptions, setLocationOptions] = useState<LocationOptionsState>({
    startLoc: [{ states: [], districts: [] }],
    endLoc: [{ states: [], districts: [] }],
    destination: [{ states: [], districts: [] }],
  });

  useEffect(() => {
    const states = CountryStateDistrict.getStatesByCountryId(1) as any;
    setLocationOptions({
      startLoc: [{ states, districts: [] }],
      endLoc: [{ states, districts: [] }],
      destination: [{ states, districts: [] }],
    });
  }, []);

  const handleLocationChange = (
    locationType: "startLoc" | "endLoc" | "destination",
    locationIndex: number,
    field: string,
    value: string
  ) => {
    formik.setFieldValue(field, value);

    if (field.includes("country")) {
      // Fetch states based on country ID (India ID = 1)
      const states = CountryStateDistrict.getStatesByCountryId(1);
      setLocationOptions((prev) => ({
        ...prev,
        [locationType]: prev[locationType].map((loc, index) =>
          index === locationIndex ? { states, districts: [] } : loc
        ),
      }));
      formik.setFieldValue(`location.${locationType}[${locationIndex}].state`, "");
      formik.setFieldValue(`location.${locationType}[${locationIndex}].district`, "");
    }

    if (field.includes("state")) {
      const stateId = CountryStateDistrict.getAllStates().find(
        (s) => s.name === value
      )?.id;
      const districts = CountryStateDistrict.getCitiesByStateId(stateId || 0);
      setLocationOptions((prev) => ({
        ...prev,
        [locationType]: prev[locationType].map((loc, index) =>
          index === locationIndex ? { ...loc, districts } : loc
        ),
      }));
      formik.setFieldValue(`location.${locationType}[${locationIndex}].district`, "");
    }
  };

  const isInternational = formik.values.tripScope === INTERNATIONAL;

  // Switching scope resets every location. Domestic => India + state + district.
  // International => country selectable (start/end also pick state + auto airport;
  // destination is country-only).
  const handleScopeChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const scope = e.target.value;
    formik.setFieldValue("tripScope", scope);

    const goingIntl = scope === INTERNATIONAL;
    const country = goingIntl ? "" : DEFAULT_DOMESTIC_COUNTRY;
    const resetLocations = (arr: any[] = [{}]) =>
      (arr.length ? arr : [{}]).map(() => ({
        country,
        state: "",
        district: "",
        airport: "",
      }));

    formik.setFieldValue("location.startLoc", resetLocations(formik.values.location.startLoc));
    formik.setFieldValue("location.endLoc", resetLocations(formik.values.location.endLoc));
    formik.setFieldValue("location.destination", [
      { country, state: "", district: "", airport: "" },
    ]);

    // Domestic uses locationOptions for state lists; international computes them inline.
    const states = goingIntl ? [] : (CountryStateDistrict.getStatesByCountryId(1) as any);
    const optionRow = (arr: any[] = [{}]) =>
      (arr.length ? arr : [{}]).map(() => ({ states, districts: [] }));
    setLocationOptions({
      startLoc: optionRow(formik.values.location.startLoc),
      endLoc: optionRow(formik.values.location.endLoc),
      destination: [{ states, districts: [] }],
    });
  };

  const addLocation = (locationType: "startLoc" | "endLoc") => {
    const currentLocations = formik.values.location[locationType] || [];
    const newLocation = {
      country: isInternational ? "" : DEFAULT_DOMESTIC_COUNTRY,
      state: "",
      district: "",
      airport: "",
    };

    formik.setFieldValue(`location.${locationType}`, [...currentLocations, newLocation]);

    const states = isInternational ? [] : CountryStateDistrict.getStatesByCountryId(1);
    setLocationOptions((prev) => ({
      ...prev,
      [locationType]: [...prev[locationType], { states, districts: [] }],
    }));
  };

  const removeLocation = (locationType: "startLoc" | "endLoc", index: number) => {
    const currentLocations = formik.values.location[locationType] || [];
    if (currentLocations.length > 1) {
      const updatedLocations = currentLocations.filter((_, i) => i !== index);
      formik.setFieldValue(`location.${locationType}`, updatedLocations);
      
      setLocationOptions((prev) => ({
        ...prev,
        [locationType]: prev[locationType].filter((_, i) => i !== index),
      }));
    }
  };

  const handleWeekendTripChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue("weekend", event.target.checked);
  };

  const renderLocationFields = (
    locationType: "startLoc" | "endLoc" | "destination",
    label: string
  ) => {
    const locations = formik.values.location[locationType] || [{ country: "India", state: "", district: "" }];
    const isMultipleAllowed = locationType !== "destination";
    // International: destination = visiting country only; start/end = country -> state -> airport.
    const intlDestination = isInternational && locationType === "destination";
    const intlStartEnd = isInternational && locationType !== "destination";
    
    return (
      <>
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
          <Typography
            variant="h6"
            sx={{
              color: "#414651",
              fontFamily: "Inter",
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: "20px",
              textAlign: "left",
            }}
          >
            {label}*
          </Typography>
          {isMultipleAllowed && (
            <Button
              startIcon={<AddIcon />}
              onClick={() => addLocation(locationType)}
              size="small"
              variant="outlined"
              sx={{ textTransform: "none" }}
            >
              Add {locationType === "startLoc" ? "Start" : "End"} Location
            </Button>
          )}
        </Box>
        
        {locations.map((location: any, index: number) => (
          <Box key={index} sx={{ mb: 2 }}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              {isMultipleAllowed && locations.length > 1 && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    {label} {index + 1}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => removeLocation(locationType, index)}
                    color="error"
                    sx={{ ml: "auto" }}
                  >
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                </>
              )}
            </Box>
            <Box display="flex" gap={2}>
              {intlDestination ? (
                /* International destination: visiting country only. */
                <DynamicInput
                  name={`location.${locationType}[${index}].country`}
                  type="select"
                  placeholder="Select country"
                  required
                  formik={formik}
                  options={COUNTRY_OPTIONS}
                  onChange={(e) =>
                    formik.setFieldValue(
                      `location.${locationType}[${index}].country`,
                      e.target.value
                    )
                  }
                />
              ) : intlStartEnd ? (
                /* International start/end: country -> state -> auto-selected airport. */
                <>
                  <DynamicInput
                    name={`location.${locationType}[${index}].country`}
                    type="select"
                    placeholder="Select country"
                    required
                    formik={formik}
                    options={AIRPORT_COUNTRY_OPTIONS}
                    onChange={(e) => {
                      const country = e.target.value;
                      formik.setFieldValue(`location.${locationType}[${index}].country`, country);
                      formik.setFieldValue(`location.${locationType}[${index}].state`, "");
                      formik.setFieldValue(`location.${locationType}[${index}].airport`, "");
                    }}
                  />
                  <DynamicInput
                    name={`location.${locationType}[${index}].state`}
                    type="select"
                    placeholder="Select State"
                    required
                    formik={formik}
                    options={getStatesForCountry(location.country)}
                    onChange={(e) => {
                      const state = e.target.value;
                      formik.setFieldValue(`location.${locationType}[${index}].state`, state);
                      formik.setFieldValue(
                        `location.${locationType}[${index}].airport`,
                        getPrimaryAirport(location.country, state)
                      );
                    }}
                    disabled={!location.country}
                  />
                  <DynamicInput
                    name={`location.${locationType}[${index}].airport`}
                    type="text"
                    placeholder="International airport (auto-selected)"
                    required
                    formik={formik}
                    value={location.airport || ""}
                    disabled
                  />
                </>
              ) : (
                <>
                  <DynamicInput
                    name={`location.${locationType}[${index}].country`}
                    type="text"
                    placeholder="Select country"
                    required
                    formik={formik}
                    value="India"
                    disabled
                  />
                  <DynamicInput
                    name={`location.${locationType}[${index}].state`}
                    type="select"
                    placeholder="Select State"
                    required
                    formik={formik}
                    options={locationOptions[locationType][index]?.states.map((s: any) => ({
                      value: s.name,
                      label: s.name,
                    })) || []}
                    onChange={(e) =>
                      handleLocationChange(
                        locationType,
                        index,
                        `location.${locationType}[${index}].state`,
                        e.target.value
                      )
                    }
                  />
                  <DynamicInput
                    name={`location.${locationType}[${index}].district`}
                    type="select"
                    placeholder="Select District/Town"
                    required
                    formik={formik}
                    options={locationOptions[locationType][index]?.districts.map((d: any) => ({
                      value: d.name,
                      label: d.name,
                    })) || []}
                    onChange={(e) =>
                      handleLocationChange(
                        locationType,
                        index,
                        `location.${locationType}[${index}].district`,
                        e.target.value
                      )
                    }
                    disabled={!formik.values.location[locationType][index]?.state}
                  />
                </>
              )}
            </Box>
          </Box>
        ))}
      </>
    );
  };

  return (
    <Card sx={{ marginTop: 3, padding: 4, width: "100%" }}>
      <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"}>
        <Typography fontWeight={600} variant="h6" color="#181D27">
          Trip Information
        </Typography>
        
        <FormControlLabel
          control={
            <Checkbox
              checked={formik.values.weekend || false}
              onChange={handleWeekendTripChange}
              name="weekendTrip"
              color="primary"
            />
          }
          label={
            <Typography fontWeight={600} variant="body1" color="#181D27">
              Weekend Trip
            </Typography>
          }
        />
      </Box>
      
      {/* Domestic / International scope */}
      <DynamicInput
        name="tripScope"
        type="select"
        label="Trip Scope"
        placeholder="Select trip scope"
        required
        formik={formik}
        options={TRIP_SCOPE}
        onChange={handleScopeChange}
      />

      {/* Trip Title */}
      <DynamicInput
        name="title"
        type="text"
        label="Trip Title "
        required
        formik={formik}
        placeholder="Enter trip title"
        limit={25}
      />

      <DynamicInput
        name="intro"
        type="textarea"
        label="Trip Intro"
        required
        formik={formik}
        placeholder="Enter trip intro"
        limit={500}
      />

      <Box flex={1} mt={2} display={"flex"} gap={4}>
        <DynamicInput
          name="minimumAge"
          type="select"
          label="Minimum Age"
          placeholder="Select minimum age"
          required
          formik={formik}
          options={TRIPAGEOPTION}
        />
        <DynamicInput
          name="maximumAge"
          type="select"
          label="Maximum Age"
          placeholder="Select maximum age"
          required
          formik={formik}
          options={TRIPAGEOPTION}
        />
      </Box>

      <Box flex={1} mt={2} display={"flex"} gap={4}>
        <DynamicInput
          name="group"
          type="select"
          label="Group Type"
          placeholder="Select group type"
          required
          formik={formik}
          options={GROUP_TYPE}
        />
        <DynamicInput
          name="tripType"
          type="select"
          label="Trip Type"
          placeholder="Select trip type"
          required
          formik={formik}
          options={TRIP_TYPE}
        />
      </Box>
      
      <Box flex={1} mt={2} display={"flex"} gap={4}>
        <DynamicInput
          name="nights"
          type="select"
          label="Nights"
          placeholder="Select number of nights"
          required
          formik={formik}
          options={TRIPDAYSOPTION}
        />
        <DynamicInput
          name="days"
          type="select"
          label="Days"
          placeholder="Select number of days"
          required
          formik={formik}
          options={TRIPDAYSOPTION}
        />
      </Box>

      {/* Start Locations */}
      {renderLocationFields("startLoc", "Trip start location")}

      {/* End Locations */}
      {renderLocationFields("endLoc", "Trip end location")}

      {/* Destination */}
      {renderLocationFields("destination", "Trip destination")}
    </Card>
  );
};

export default TripInfoForm;